import {
  addDoc,
  collection,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { checkIntegrationReadiness, recordAutomationIntegrationMissing } from './integrationGatewayService'
import { isFunPackEnabledForActor } from './funPackAccess'
import {
  recordAutomationRun,
  type AutomationActor,
} from './automationService'
import {
  buildSimulatedOutcome,
  evaluateCondition,
  triggerMatchesEvent,
  validateAction,
} from './automationRegistry'
import { renderAutomationText } from './automation/placeholders.js'
import type {
  AutomationAction,
  AutomationActionRunEntry,
  AutomationConstraint,
  AutomationDefinition,
  AutomationGraph,
  AutomationRunOutcome,
  AutomationTriggerEvent,
  CreateNodeActionConfig,
  NotifyUserActionConfig,
  UpdateFieldActionConfig,
  WebhookActionConfig,
} from '../types/automation'

const triggerMatches = (automation: AutomationDefinition, event: AutomationTriggerEvent): boolean => {
  if (automation.status !== 'ACTIVE') return false
  if (automation.trigger.type !== event.type) return false
  if (event.type === 'NODE_CREATED' && automation.trigger.type === 'NODE_CREATED') {
    const cfg = automation.trigger.config
    if (cfg.boardKey && event.payload.boardKey && cfg.boardKey !== event.payload.boardKey) return false
    if (cfg.nodeKind && cfg.nodeKind !== 'ANY' && cfg.nodeKind !== event.payload.nodeKind) return false
  }
  if (event.type === 'FORM_SUBMITTED' && automation.trigger.type === 'FORM_SUBMITTED') {
    const cfg = automation.trigger.config
    if (cfg.formId && cfg.formId !== event.payload.formId) return false
  }
  if (event.type === 'UPLOAD_COMPLETED' && automation.trigger.type === 'UPLOAD_COMPLETED') {
    const cfg = automation.trigger.config
    if (cfg.providerId && cfg.providerId !== 'ANY' && event.payload.providerId && cfg.providerId !== event.payload.providerId) return false
  }
  return true
}

type ActionRunner = (
  action: AutomationAction,
  context: ActionContext,
) => Promise<{ outcome: AutomationRunOutcome; error?: string; simulatedOutcome?: string }>

/** Loose subset of AutomationDefinition that runners actually need. Lets the
 * v2 graph reuse the same runner surface without allocating a fake Definition. */
type RunnerAutomation = { id: string; workspaceId: string; name: string }

type ActionContext = {
  workspaceId: string
  actor: AutomationActor
  event: AutomationTriggerEvent
  automation: RunnerAutomation
  dryRun: boolean
  /** Shared run-scoped variables — populated by SET_VARIABLE, read by later
   * actions via {{vars.name}} once expression templating lands (Slice 4). */
  vars: Record<string, unknown>
}

const notifyUserRunner: ActionRunner = async (action, ctx) => {
  const cfg = action.config as NotifyUserActionConfig
  const simulated = buildSimulatedOutcome(action)
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
  const recipientId = cfg.recipientUserId?.trim() || ctx.event.actorId || ctx.actor.id
  if (!recipientId) return { outcome: 'FAILED', error: 'No recipient resolved for NOTIFY_USER.' }
  const placeholderCtx = {
    payload: (ctx.event.payload ?? {}) as Record<string, unknown>,
  }
  const title = cfg.title ? renderAutomationText(cfg.title, placeholderCtx).text : 'Automation fired'
  const body = cfg.body ? renderAutomationText(cfg.body, placeholderCtx).text : ''
  await addDoc(collection(firestoreDb, 'notifications'), {
    recipientId,
    actorId: ctx.actor.id,
    workspaceId: ctx.workspaceId,
    workspaceAudience: null,
    eventType: 'SYSTEM_NOTICE',
    entityType: 'SYSTEM',
    entityId: ctx.automation.id,
    entityTitle: ctx.automation.name,
    priority: 'LOW',
    channels: ['IN_APP'],
    title,
    body,
    guardianPersonaId: null,
    deeplink: null,
    batchKey: null,
    dedupeKey: `automation:${ctx.automation.id}:${ctx.event.eventId}:${action.id}`,
    readAt: null,
    dismissedAt: null,
    createdAt: serverTimestamp(),
    expiresAt: null,
    delivery: { inApp: 'PENDING' },
    data: { automationId: ctx.automation.id, eventType: ctx.event.type },
  })
  return { outcome: 'SUCCESS' }
}

const createNodeRunner: ActionRunner = async (action, ctx) => {
  const simulated = buildSimulatedOutcome(action)
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
  const cfg = action.config as CreateNodeActionConfig
  if (!cfg.title.trim()) return { outcome: 'FAILED', error: 'CREATE_NODE missing title.' }
  // Phase 1 stub: log a queued action; real node creation lands when we move
  // the orchestrator server-side in Phase 2.
  await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
    workspaceId: ctx.workspaceId,
    automationId: ctx.automation.id,
    actionType: 'CREATE_NODE',
    config: cfg,
    eventId: ctx.event.eventId,
    createdAt: serverTimestamp(),
  })
  return { outcome: 'SUCCESS' }
}

const updateFieldRunner: ActionRunner = async (action, ctx) => {
  const simulated = buildSimulatedOutcome(action)
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
  const cfg = action.config as UpdateFieldActionConfig
  if (!cfg.fieldPath) return { outcome: 'FAILED', error: 'UPDATE_FIELD missing fieldPath.' }
  await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
    workspaceId: ctx.workspaceId,
    automationId: ctx.automation.id,
    actionType: 'UPDATE_FIELD',
    config: cfg,
    eventId: ctx.event.eventId,
    createdAt: serverTimestamp(),
  })
  return { outcome: 'SUCCESS' }
}

const webhookRunner: ActionRunner = async (action, ctx) => {
  const simulated = buildSimulatedOutcome(action)
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
  const cfg = action.config as WebhookActionConfig
  const readiness = await checkIntegrationReadiness({ workspaceId: ctx.workspaceId, providerId: cfg.providerId, connectionId: cfg.connectionId })
  if (!readiness.isReady) {
    await recordAutomationIntegrationMissing({ workspaceId: ctx.workspaceId, providerId: cfg.providerId, actionKey: 'AUTOMATION_EXTERNAL_WEBHOOK', automationId: ctx.automation.id })
    return { outcome: 'SKIPPED', error: `INTEGRATION_MISSING:${cfg.providerId}` }
  }
  // Phase 1 stub: server-side webhook dispatch ships in Phase 2.
  await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
    workspaceId: ctx.workspaceId,
    automationId: ctx.automation.id,
    actionType: 'WEBHOOK',
    config: cfg,
    eventId: ctx.event.eventId,
    createdAt: serverTimestamp(),
  })
  return { outcome: 'SUCCESS' }
}

// v2 action stubs — same Phase-1 pattern as CREATE_NODE / UPDATE_FIELD: queue
// the intent to `automation_pending_actions` for the server-side worker to
// pick up. DELAY and SET_VARIABLE are handled inline by the runner (no queue).
const makePendingRunner = (actionType: AutomationAction['type']): ActionRunner =>
  async (action, ctx) => {
    const simulated = buildSimulatedOutcome(action)
    if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
    await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
      workspaceId: ctx.workspaceId,
      automationId: ctx.automation.id,
      actionType,
      config: action.config,
      eventId: ctx.event.eventId,
      createdAt: serverTimestamp(),
    })
    return { outcome: 'SUCCESS' }
  }

const delayRunner: ActionRunner = async (action, ctx) => {
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: buildSimulatedOutcome(action) }
  // Client-side DELAY is a no-op — the pending-action worker handles real waits.
  await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
    workspaceId: ctx.workspaceId,
    automationId: ctx.automation.id,
    actionType: 'DELAY',
    config: action.config,
    eventId: ctx.event.eventId,
    createdAt: serverTimestamp(),
  })
  return { outcome: 'SUCCESS' }
}

const setVariableRunner: ActionRunner = async (action, ctx) => {
  // Purely in-run — mutations to ctx.vars are visible to later actions in this run.
  const cfg = action.config as { name: string; value: string }
  if (cfg.name) ctx.vars[cfg.name] = cfg.value
  return {
    outcome: ctx.dryRun ? 'SIMULATED' : 'SUCCESS',
    simulatedOutcome: ctx.dryRun ? buildSimulatedOutcome(action) : undefined,
  }
}

const awardXpRunner: ActionRunner = async (action, ctx) => {
  const simulated = buildSimulatedOutcome(action)
  if (ctx.dryRun) return { outcome: 'SIMULATED', simulatedOutcome: simulated }
  const cfg = action.config as { recipientUserId?: string; amount?: number }
  const isActorRecipient = !cfg?.recipientUserId || cfg.recipientUserId === ctx.actor.id

  if (isActorRecipient) {
    const enabled = await isFunPackEnabledForActor(ctx.workspaceId, ctx.actor.id)
    if (!enabled) {
      return { outcome: 'SKIPPED', error: 'Fun pack is disabled for recipient' }
    }
  } else {
    // Server worker must check recipient funPack status before awarding XP (no worker exists; functions/src/automationEngine.ts:1453 refuses AWARD_XP).
  }

  await addDoc(collection(firestoreDb, 'automation_pending_actions'), {
    workspaceId: ctx.workspaceId,
    automationId: ctx.automation.id,
    actionType: 'AWARD_XP',
    config: action.config,
    eventId: ctx.event.eventId,
    createdAt: serverTimestamp(),
  })
  return { outcome: 'SUCCESS' }
}

const RUNNERS: Record<AutomationAction['type'], ActionRunner> = {
  NOTIFY_USER: notifyUserRunner,
  CREATE_NODE: createNodeRunner,
  UPDATE_FIELD: updateFieldRunner,
  WEBHOOK: webhookRunner,
  COMPLETE_NODE: makePendingRunner('COMPLETE_NODE'),
  ASSIGN_TASK: makePendingRunner('ASSIGN_TASK'),
  CREATE_CHECKLIST_ITEM: makePendingRunner('CREATE_CHECKLIST_ITEM'),
  SEND_CHAT_MESSAGE: makePendingRunner('SEND_CHAT_MESSAGE'),
  GRANT_CREDITS: makePendingRunner('GRANT_CREDITS'),
  DEDUCT_CREDITS: makePendingRunner('DEDUCT_CREDITS'),
  AWARD_XP: awardXpRunner,
  LOG_TO_LEDGER: makePendingRunner('LOG_TO_LEDGER'),
  ADVANCE_BOOK_WINDOW: makePendingRunner('ADVANCE_BOOK_WINDOW'),
  DELAY: delayRunner,
  SET_VARIABLE: setVariableRunner,
}

const summarizeEvent = (event: AutomationTriggerEvent): string => {
  switch (event.type) {
    case 'NODE_CREATED':
      return `Node ${event.payload.nodeKind} "${event.payload.title ?? event.payload.nodeId}" created`
    case 'CHECKLIST_CREATED':
      return `Checklist "${event.payload.title ?? event.payload.nodeId}" created`
    case 'CHECKLIST_COMPLETED':
      return `Checklist "${event.payload.title ?? event.payload.nodeId}" completed`
    case 'PROOF_UPDATED':
      return `Proof updated for "${event.payload.title ?? event.payload.nodeId}"`
    case 'TASK_ASSIGNED':
      return `Task "${event.payload.title ?? event.payload.nodeId}" assigned`
    case 'FORM_SUBMITTED':
      return `Form ${event.payload.formId} submitted`
    case 'UPLOAD_COMPLETED':
      return `Upload ${event.payload.artifactId} completed`
    case 'DATE_REACHED':
      return `Schedule ${event.payload.scheduleKey} reached`
    case 'NODE_COMPLETED':
      return `Node ${event.payload.nodeKind} "${event.payload.title ?? event.payload.nodeId}" completed`
    case 'AI_ESSAY_GENERATED':
      return 'AI essay generated'
    case 'AI_REFINE_USED':
      return 'AI refine used'
    case 'FOCUS_BLOCK_COMPLETED':
      return `Focus block completed for ${event.payload.minutes} minutes`
    case 'SPIN_WON':
      return `Spin won at ${event.payload.tier}`
    case 'CHAT_MESSAGE_POSTED':
      return `Chat message ${event.payload.messageId} posted`
  }
  return `Automation event ${event.type}`
}

export type RunAutomationOptions = {
  automation: AutomationDefinition
  event: AutomationTriggerEvent
  actor: AutomationActor
  dryRun?: boolean
}

export const runAutomation = async ({
  automation,
  event,
  actor,
  dryRun = false,
}: RunAutomationOptions): Promise<AutomationActionRunEntry[]> => {
  const startedAt = Timestamp.now()
  const entries: AutomationActionRunEntry[] = []
  const vars: Record<string, unknown> = {}
  let finalOutcome: AutomationRunOutcome = 'SUCCESS'
  let errorMessage: string | undefined

  for (const action of automation.actions) {
    const validation = validateAction(action)
    if (validation) {
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: 'FAILED',
        startedAt: Timestamp.now(),
        finishedAt: Timestamp.now(),
        errorMessage: validation,
      })
      finalOutcome = 'FAILED'
      errorMessage = validation
      break
    }
    const actionStarted = Timestamp.now()
    try {
      const result = await RUNNERS[action.type](action, {
        workspaceId: automation.workspaceId,
        actor,
        event,
        automation,
        dryRun,
        vars,
      })
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: result.outcome,
        startedAt: actionStarted,
        finishedAt: Timestamp.now(),
        errorMessage: result.error,
        simulatedOutcome: result.simulatedOutcome,
      })
      if (result.outcome === 'FAILED') {
        finalOutcome = 'FAILED'
        errorMessage = result.error
        break
      }
      if (result.outcome === 'SKIPPED') {
        finalOutcome = 'SKIPPED'
        errorMessage = result.error
        break
      }
      if (result.outcome === 'SIMULATED') finalOutcome = 'SIMULATED'
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: 'FAILED',
        startedAt: actionStarted,
        finishedAt: Timestamp.now(),
        errorMessage: message,
      })
      finalOutcome = 'FAILED'
      errorMessage = message
      break
    }
  }

  await recordAutomationRun(
    automation.workspaceId,
    actor,
    automation.id,
    automation.name,
    {
      triggeredBy: actor.id,
      triggerEventId: event.eventId,
      triggerEventSummary: summarizeEvent(event),
      outcome: finalOutcome,
      startedAt,
      finishedAt: Timestamp.now(),
      actionEntries: entries,
      errorMessage,
    },
    { skipAudit: errorMessage?.startsWith('INTEGRATION_MISSING:') === true },
  )
  return entries
}

export const dispatchAutomationEvent = async (
  automations: AutomationDefinition[],
  event: AutomationTriggerEvent,
  actor: AutomationActor,
): Promise<void> => {
  const matches = automations.filter((automation) => triggerMatches(automation, event))
  if (matches.length === 0) return
  await Promise.allSettled(
    matches.map((automation) => runAutomation({ automation, event, actor })),
  )
}

// ---------------------------------------------------------------------------
// Internal event bus — for system listeners (e.g. QuotaService) that should
// react to TriggerEvents without being modelled as user-configurable
// AutomationDefinitions. Subscribers are called best-effort in parallel and
// failures are isolated (never bubble back to the dispatcher).
// ---------------------------------------------------------------------------

export type InternalListener = (
  event: AutomationTriggerEvent,
  actor: AutomationActor,
) => Promise<void> | void

const internalListeners = new Map<AutomationTriggerEvent['type'], Set<InternalListener>>()

export const subscribeInternal = (
  type: AutomationTriggerEvent['type'],
  listener: InternalListener,
): (() => void) => {
  let set = internalListeners.get(type)
  if (!set) {
    set = new Set()
    internalListeners.set(type, set)
  }
  set.add(listener)
  return () => {
    set?.delete(listener)
  }
}

export const emitInternal = async (
  event: AutomationTriggerEvent,
  actor: AutomationActor,
): Promise<void> => {
  const set = internalListeners.get(event.type)
  if (!set || set.size === 0) return
  await Promise.allSettled(
    Array.from(set).map(async (listener) => {
      try {
        await listener(event, actor)
      } catch (error) {
        console.warn('[automationOrchestrator] internal listener failed', { type: event.type }, error)
      }
    }),
  )
}

export const simulateAutomation = async (
  automation: AutomationDefinition,
  event: AutomationTriggerEvent,
  actor: AutomationActor,
): Promise<AutomationActionRunEntry[]> => runAutomation({ automation, event, actor, dryRun: true })

export const buildSyntheticEvent = (
  automation: AutomationDefinition,
  actor: AutomationActor,
): AutomationTriggerEvent => {
  const eventId = `sim-${automation.id}-${Date.now()}`
  const base = { workspaceId: automation.workspaceId, eventId, actorId: actor.id }
  switch (automation.trigger.type) {
    case 'NODE_CREATED':
    case 'CHECKLIST_CREATED':
    case 'CHECKLIST_COMPLETED':
    case 'PROOF_UPDATED':
    case 'TASK_ASSIGNED':
      return {
        type: automation.trigger.type,
        ...base,
        payload: { nodeId: 'sim-node', nodeKind: automation.trigger.config.nodeKind && automation.trigger.config.nodeKind !== 'ANY' ? automation.trigger.config.nodeKind : 'TASK', boardKey: automation.trigger.config.boardKey, title: 'Simulated node' },
      }
    case 'FORM_SUBMITTED':
      return { type: 'FORM_SUBMITTED', ...base, payload: { formId: automation.trigger.config.formId ?? 'sim-form', responseId: 'sim-response' } }
    case 'UPLOAD_COMPLETED':
      return { type: 'UPLOAD_COMPLETED', ...base, payload: { artifactId: 'sim-artifact', providerId: undefined } }
    case 'DATE_REACHED':
      return { type: 'DATE_REACHED', ...base, payload: { scheduleKey: 'sim-schedule', isoDate: new Date().toISOString() } }
    case 'NODE_COMPLETED':
      return { type: 'NODE_COMPLETED', ...base, payload: { nodeId: 'sim-node', nodeKind: 'TASK', boardKey: undefined, title: 'Simulated node' } }
    case 'AI_ESSAY_GENERATED':
      return { type: 'AI_ESSAY_GENERATED', ...base, payload: { creditsSpent: 5 } }
    case 'AI_REFINE_USED':
      return { type: 'AI_REFINE_USED', ...base, payload: { creditsSpent: 2 } }
    case 'FOCUS_BLOCK_COMPLETED':
      return { type: 'FOCUS_BLOCK_COMPLETED', ...base, payload: { minutes: 25 } }
    case 'SPIN_WON':
      return { type: 'SPIN_WON', ...base, payload: { tier: 'LUCKY_SPIN' } }
    case 'CHAT_MESSAGE_POSTED':
      return { type: 'CHAT_MESSAGE_POSTED', ...base, payload: { channelId: 'sim-channel', messageId: 'sim-msg', hasMentions: true } }
  }
}

// ---------------------------------------------------------------------------
// v2 — AutomationGraph runner
// Executes multi-trigger + constraint + IF/ELSE flows. Reuses the v1 RUNNERS
// map so all action types (v1 and v2) work identically.
// ---------------------------------------------------------------------------

type RateLimitStore = {
  /** In-memory counter keyed by `${graphId}:${entityId ?? '*'}`. Best-effort
   * client-side gate; real durable rate-limiting will land server-side. */
  windows: Map<string, { count: number; windowStartMs: number; lastFireMs: number }>
}
const rateStore: RateLimitStore = { windows: new Map() }

const evaluateConstraint = (
  constraint: AutomationConstraint | undefined,
  ctx: {
    graphId: string
    event: AutomationTriggerEvent
    actor: AutomationActor & { role?: string; walletCredits?: number; planTier?: string }
    runContext: Parameters<typeof evaluateCondition>[0]
    now: number
  },
): { pass: true } | { pass: false; reason: string } => {
  if (!constraint) return { pass: true }
  if (constraint.filter && !evaluateCondition(ctx.runContext, constraint.filter)) {
    return { pass: false, reason: 'FILTER_MISMATCH' }
  }
  if (constraint.quota) {
    const q = constraint.quota
    if (typeof q.minWalletCredits === 'number' && (ctx.actor.walletCredits ?? 0) < q.minWalletCredits) {
      return { pass: false, reason: 'QUOTA_WALLET' }
    }
    if (q.actorRole && ctx.actor.role !== q.actorRole) {
      return { pass: false, reason: 'QUOTA_ROLE' }
    }
    if (q.minPlanTier) {
      const tierOrder = { FREE: 0, PRO: 1, TEAM: 2 } as const
      const actorTier = (ctx.actor.planTier ?? 'FREE') as keyof typeof tierOrder
      if (tierOrder[actorTier] < tierOrder[q.minPlanTier]) return { pass: false, reason: 'QUOTA_PLAN' }
    }
  }
  if (constraint.rate) {
    const r = constraint.rate
    const now = ctx.now
    if (r.hoursOfDay) {
      const hr = new Date(now).getHours()
      const { start, end } = r.hoursOfDay
      const inWindow = start <= end ? hr >= start && hr < end : hr >= start || hr < end
      if (!inWindow) return { pass: false, reason: 'RATE_HOURS' }
    }
    if (typeof r.weekdayMask === 'number') {
      const dow = new Date(now).getDay()
      if ((r.weekdayMask & (1 << dow)) === 0) return { pass: false, reason: 'RATE_WEEKDAY' }
    }
    // event-derived entity id — used for cooldown-per-entity dedup
    const entityId = ('payload' in ctx.event ? (ctx.event.payload as Record<string, unknown>) : {}) as Record<string, unknown>
    const entityKey = String(entityId.nodeId ?? entityId.taskId ?? entityId.formId ?? entityId.artifactId ?? '*')
    const bucketKey = `${ctx.graphId}:${entityKey}`
    const bucket = rateStore.windows.get(bucketKey)
    if (bucket) {
      if (r.cooldownPerEntityMs && now - bucket.lastFireMs < r.cooldownPerEntityMs) {
        return { pass: false, reason: 'RATE_COOLDOWN' }
      }
      if (now - bucket.windowStartMs > r.windowMs) {
        bucket.count = 0
        bucket.windowStartMs = now
      }
      if (bucket.count >= r.maxPerWindow) return { pass: false, reason: 'RATE_WINDOW' }
      bucket.count += 1
      bucket.lastFireMs = now
    } else {
      rateStore.windows.set(bucketKey, { count: 1, windowStartMs: now, lastFireMs: now })
    }
  }
  return { pass: true }
}

export type RunGraphOptions = {
  graph: AutomationGraph
  event: AutomationTriggerEvent
  actor: AutomationActor & { role?: string; walletCredits?: number; planTier?: string }
  dryRun?: boolean
}

export const runAutomationGraph = async ({
  graph,
  event,
  actor,
  dryRun = false,
}: RunGraphOptions): Promise<{
  outcome: AutomationRunOutcome
  branch: 'THEN' | 'ELSE' | 'NONE'
  entries: AutomationActionRunEntry[]
  skipReason?: string
}> => {
  const runContext = {
    trigger: { type: event.type as AutomationGraph['triggers'][number]['type'] },
    payload: ('payload' in event ? (event.payload as Record<string, unknown>) : {}) as Record<string, unknown>,
    actor: { id: actor.id, role: actor.role, email: undefined as string | undefined },
    vars: {} as Record<string, unknown>,
  }

  const constraintOutcome = evaluateConstraint(graph.constraint, {
    graphId: graph.id,
    event,
    actor,
    runContext,
    now: Date.now(),
  })
  if (!constraintOutcome.pass) {
    return { outcome: 'SKIPPED', branch: 'NONE', entries: [], skipReason: constraintOutcome.reason }
  }

  const branchIsThen = graph.if ? evaluateCondition(runContext, graph.if) : true
  const actions = branchIsThen ? graph.then : (graph.else ?? [])
  const branch: 'THEN' | 'ELSE' | 'NONE' = graph.if ? (branchIsThen ? 'THEN' : 'ELSE') : 'NONE'
  if (actions.length === 0) {
    return { outcome: 'SKIPPED', branch, entries: [], skipReason: 'NO_ACTIONS_IN_BRANCH' }
  }

  const entries: AutomationActionRunEntry[] = []
  let finalOutcome: AutomationRunOutcome = 'SUCCESS'
  for (const action of actions) {
    const validation = validateAction(action)
    if (validation) {
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: 'FAILED',
        startedAt: Timestamp.now(),
        finishedAt: Timestamp.now(),
        errorMessage: validation,
      })
      finalOutcome = 'FAILED'
      break
    }
    const startedAt = Timestamp.now()
    try {
      const result = await RUNNERS[action.type](action, {
        workspaceId: graph.workspaceId,
        actor,
        event,
        automation: { id: graph.id, workspaceId: graph.workspaceId, name: graph.name },
        dryRun,
        vars: runContext.vars,
      })
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: result.outcome,
        startedAt,
        finishedAt: Timestamp.now(),
        errorMessage: result.error,
        simulatedOutcome: result.simulatedOutcome,
      })
      if (result.outcome === 'FAILED') { finalOutcome = 'FAILED'; break }
      if (result.outcome === 'SKIPPED') { finalOutcome = 'SKIPPED'; break }
      if (result.outcome === 'SIMULATED') finalOutcome = 'SIMULATED'
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      entries.push({
        actionId: action.id,
        type: action.type,
        outcome: 'FAILED',
        startedAt,
        finishedAt: Timestamp.now(),
        errorMessage: message,
      })
      finalOutcome = 'FAILED'
      break
    }
  }
  return { outcome: finalOutcome, branch, entries }
}

/** Dispatch a trigger event to BOTH v1 AutomationDefinitions and v2 AutomationGraphs.
 * Legacy definitions match single-trigger; graphs match against any-of triggers. */
export const dispatchAutomationEventV2 = async (
  automations: AutomationDefinition[],
  graphs: AutomationGraph[],
  event: AutomationTriggerEvent,
  actor: AutomationActor & { role?: string; walletCredits?: number; planTier?: string },
): Promise<void> => {
  const v1Matches = automations.filter((automation) => {
    if (automation.status !== 'ACTIVE') return false
    return triggerMatchesEvent(automation.trigger, event)
  })
  const v2Matches = graphs.filter((graph) => {
    if (graph.status !== 'ACTIVE') return false
    return graph.triggers.some((trigger) => triggerMatchesEvent(trigger, event))
  })
  if (v1Matches.length === 0 && v2Matches.length === 0) return
  await Promise.allSettled([
    ...v1Matches.map((automation) => runAutomation({ automation, event, actor })),
    ...v2Matches.map((graph) => runAutomationGraph({ graph, event, actor })),
  ])
}
